"use client";

import { useState, useEffect } from "react";
import { Award, FileCheck, Globe, Lock, ShieldCheck, UserCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standardsEn = [{
  icon: ShieldCheck,
  title: "BV Certification",
  description: "Bureau Veritas verified manufacturing protocols ensuring consistent quality control and operational excellence across our facilities."
}, {
  icon: Lock,
  title: "ITS Audited",
  description: "Intertek Testing Services validation for factory safety, ethical labor practices, and environmental management systems."
}, {
  icon: Globe,
  title: "SGS Compliance",
  description: "Comprehensive SGS inspections confirming adherence to international toy safety standards and material chemical requirements."
}, {
  icon: FileCheck,
  title: "TUV Rheinland",
  description: "Rigorous TUV testing for product durability and mechanical safety, meeting the highest benchmarks for global consumer goods."
}, {
  icon: UserCheck,
  title: "CCC Certified",
  description: "China Compulsory Certification for all domestic market products, ensuring full regulatory alignment for the Chinese consumer sector."
}, {
  icon: Award,
  title: "G-Mark Standards",
  description: "Gulf States certification compliance, verifying that our products meet the specific safety and quality mandates for the Middle East market."
}];
const standardsZh = [{
  icon: ShieldCheck,
  title: "BV 認證",
  description: "Bureau Veritas 驗證之製造規範，確保我們所有廠房維持嚴謹一致的品質控制與卓越運營。"
}, {
  icon: Lock,
  title: "ITS 審核",
  description: "Intertek 檢測服務驗證，涵蓋工廠安全、道德勞工標準及環境管理體系。"
}, {
  icon: Globe,
  title: "SGS 合規",
  description: "全面之 SGS 檢驗，確認符合國際玩具安全標準及材料化學安全要求。"
}, {
  icon: FileCheck,
  title: "TÜV 萊茵認證",
  description: "嚴謹的 TÜV 檢測，驗證產品耐用度與機械物理安全，達到全球消費品最高標準。"
}, {
  icon: UserCheck,
  title: "CCC 認證",
  description: "適用於所有國內市場產品的中國強制性產品認證（CCC），確保全面符合中國消費市場法規。"
}, {
  icon: Award,
  title: "G-Mark 標準",
  description: "海灣阿拉伯國家 G-Mark 認證合規，確保產品符合中東市場特定安全與品質法規要求。"
}];
const partnerLogos = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/logo_bureau_veritas.png",
  alt: "Bureau Veritas Logo"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/logo_intertek.png",
  alt: "Intertek Logo"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/logo_sgs.png",
  alt: "SGS Logo"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/logo_tuv_rheinland.png",
  alt: "TUV Rheinland Logo"
}];
export default function Compliance() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const standards = isZh ? standardsZh : standardsEn;
  return <section data-section-id="20209" id="global-safety-standards" className="bg-background text-foreground py-24 md:py-32 light">
      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-[1280px]">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[620px] text-center transition-all duration-[520ms] ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground font-default">
            {isZh ? "品質保證" : "Quality Assurance"}
          </p>
          <div className="mt-3 mx-auto h-px w-12 bg-border" />
          <h2 className="mt-6 font-default text-foreground text-[clamp(2.125rem,3.6vw,3.25rem)] font-semibold leading-[1.08] tracking-tight">
            {isZh ? <>
                合規與認證，<em className="italic font-normal">權威專家驗證</em>。
              </> : <>
                Global standards, <em className="italic font-normal">verified by experts</em>.
              </>}
          </h2>
          <p className="mt-6 text-muted-foreground text-[clamp(1.0625rem,1.05vw,1.1875rem)] leading-[1.6] font-default">
            {isZh ? "我們為您的產品檢測、合規及驗貨提供一站式全方位解決方案。我們與 BV / ITS / SGS / TÜV Rheinland 在中國及海外地區（包括英國、越南、印尼、柬埔寨等）保持良好合作關係，全力滿足您的市場需求。" : "We can support you with all services for your product testing, compliance and inspection from a one stop solution. We have good relationship with BV / ITS / SGS / TUVR over China and non-China area (i.e. UK, Vietnam, Indonesia, Cambodia etc) to meet your market need."}
          </p>
        </div>

        {}
        <div className={`mt-12 border-y border-border py-10 transition-all duration-[600ms] delay-200 ease-out ${intro.isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 items-center justify-items-center">
            {partnerLogos.map((logo, i) => <div key={i} data-index={i} className="flex items-center justify-center">
                <img src={logo.src} alt={logo.alt} className="h-10 w-auto grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300" loading="lazy" />
              </div>)}
          </div>
        </div>

        {}
        <div ref={grid.ref} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
          {standards.map((standard, i) => {
          const Icon = standard.icon;
          return <div data-index={i} key={i} className={`group flex flex-col bg-card border border-border rounded-lg p-7 transition-all duration-[240ms] ease-out hover:border-primary hover:-translate-y-1 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 60}ms` : "0ms",
            transitionProperty: "opacity, transform, border-color"
          }}>
                <div className="text-primary">
                  <Icon className="w-10 h-10" strokeWidth={1.25} aria-hidden="true" />
                </div>

                <h3 className="mt-6 font-default text-foreground text-[clamp(1.125rem,1.2vw,1.25rem)] font-semibold leading-[1.35] tracking-tight">
                  {standard.title}
                </h3>

                <div className="mt-4 h-px w-full bg-border" />

                <p className="mt-4 text-muted-foreground text-sm leading-[1.65] font-default flex-1">
                  {standard.description}
                </p>

                <div className="mt-6">
                  <Button asChild variant="link" className={`h-auto p-0 text-foreground font-default font-semibold text-sm hover:no-underline ${i === 0 ? "px-2" : ""}`}>
                    <Link to="#" className={`group/link inline-flex items-center gap-1 ${i === 0 ? "whitespace-nowrap" : ""}`}>
                      <span className="border-b border-border group-hover/link:border-b-2 group-hover/link:border-primary transition-all duration-[180ms] pb-0.5">
                        {isZh ? "索取認證詳情 \u2192" : "Request certification details \u2192"}
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}